import { useState } from 'react';
import { CalendarDays, Coffee, TriangleAlert } from 'lucide-react';
import { useGetMyChildrenTimetableQuery, getApiError, type ChildTimetable } from '@/store/api';
import { EmptyState } from '@/components/ui/EmptyState';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

const DAYS = [
  { n: 1, label: 'Monday' },
  { n: 2, label: 'Tuesday' },
  { n: 3, label: 'Wednesday' },
  { n: 4, label: 'Thursday' },
  { n: 5, label: 'Friday' },
];

/*
  ═════════════════════════════════════════════════════════════════════════════
  A PARENT'S VIEW OF THE WEEK

  A day at a time, not a grid
  ───────────────────────────
  The school's screen is a grid because a head teacher is comparing five days
  at once — that is the job. A parent is answering a different question, almost
  always about one day: "does she need her PE kit tomorrow?" A five-column
  grid at phone width answers that by making them pinch and scroll.

  So it opens on today, and the day is a list you read down.

  Why it shows the teacher's name
  ───────────────────────────────
  It is the answer to "who do I ask about the maths homework", which is a
  thing parents ring the office about and the office then has to look up.
  ═════════════════════════════════════════════════════════════════════════════
*/
export function ParentTimetablePage() {
  const { data: children = [], isLoading, isError, error } = useGetMyChildrenTimetableQuery();

  /* Opens on today, falling back to Monday at the weekend — a parent looking
     on Sunday evening is almost always asking about Monday. */
  const today = new Date().getDay();
  const [day, setDay] = useState(today >= 1 && today <= 5 ? today : 1);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (isError) {
    return (
      <div
        role="alert"
        className="flex items-start gap-2 rounded-lg border border-destructive/25 bg-destructive/[0.06] px-3 py-2.5 text-[11.5px] font-medium text-[hsl(var(--record-ink-danger))]"
      >
        <TriangleAlert size={13} className="mt-[1px] flex-shrink-0" />
        {getApiError(error)}
      </div>
    );
  }

  const anyLessons = children.some((c) => c.slots.length > 0);

  if (!anyLessons) {
    return (
      <EmptyState
        icon={<CalendarDays size={22} />}
        title="No timetable yet"
        description="The school has not published a weekly schedule for this class. It will appear here as soon as they do."
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* The day picker sits above everything: it is the one control, and it
          applies to every child at once. */}
      <div className="flex gap-1 overflow-x-auto">
        {DAYS.map((d) => (
          <button
            key={d.n}
            type="button"
            onClick={() => setDay(d.n)}
            aria-pressed={day === d.n}
            className={cn(
              'flex-1 whitespace-nowrap rounded-md border px-2 py-1.5 text-[11px] font-bold uppercase tracking-[0.04em] transition-colors',
              day === d.n
                ? 'border-primary bg-primary/10 text-foreground'
                : 'border-border text-muted-foreground hover:text-foreground',
            )}
          >
            {d.label.slice(0, 3)}
          </button>
        ))}
      </div>

      {children.map((child) => (
        <ChildDay key={child.studentId} child={child} day={day} />
      ))}
    </div>
  );
}

function ChildDay({ child, day }: { child: ChildTimetable; day: number }) {
  const slots = new Map(child.slots.filter((s) => s.day === day).map((s) => [s.periodId, s]));

  return (
    <section className="flex flex-col gap-2">
      <div>
        <h2 className="text-[13px] font-bold tracking-tight text-foreground">{child.studentName}</h2>
        <p className={styles.meta}>{child.className}</p>
      </div>

      <div className="overflow-clip rounded-lg border border-border bg-card">
        {child.periods.length === 0 ? (
          <p className="px-4 py-5 text-center text-[11.5px] text-muted-foreground">
            No schedule for this class yet.
          </p>
        ) : (
          child.periods.map((p) => {
            const slot = slots.get(p._id);
            return (
              <div
                key={p._id}
                className={cn(
                  'flex items-center gap-3 border-b border-border px-4 py-2.5 last:border-b-0',
                  !p.isTeaching && 'bg-muted/40',
                )}
              >
                <span className="w-[86px] flex-shrink-0 text-[11px] tabular-nums text-muted-foreground">
                  {p.startTime}–{p.endTime}
                </span>

                {!p.isTeaching ? (
                  <span className="flex items-center gap-1.5 text-[12px] font-semibold text-muted-foreground">
                    <Coffee size={12} />
                    {p.name}
                  </span>
                ) : slot ? (
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[12.5px] font-bold text-foreground">{slot.subject}</p>
                    {(slot.teacher || slot.room) && (
                      <p className={cn(styles.meta, 'truncate')}>
                        {[slot.teacher, slot.room].filter(Boolean).join(' · ')}
                      </p>
                    )}
                  </div>
                ) : (
                  /* An empty teaching period is shown rather than hidden: a
                     gap in the published week is something a parent can ask
                     about, and a row that vanishes cannot be asked about. */
                  <span className="text-[12px] text-muted-foreground/60">Not timetabled</span>
                )}
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}
